import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useOrderNotifications } from "./hooks/useOrderNotifications";
import { usePagePreload } from "./hooks/usePagePreload";
import Layout from "./components/Layout";
import FadeIn from "./components/FadeIn";
import { RouteAwareSkeleton } from "./components/skeletons";

// Lazy load all page components
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OrderExtrasCF = lazy(() => import("./pages/OrderExtrasCF"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const TermeniSiConditii = lazy(() => import("./pages/TermeniSiConditii"));
const PoliticaConfidentialitate = lazy(() => import("./pages/PoliticaConfidentialitate"));
const DespreNoi = lazy(() => import("./pages/DespreNoi"));
const Contact = lazy(() => import("./pages/Contact"));
const Install = lazy(() => import("./pages/Install"));
const SolutionareLitigii = lazy(() => import("./pages/SolutionareLitigii"));
const CalificareCredit = lazy(() => import("./pages/CalificareCredit"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Hub Post-Vânzare
const HubAuth = lazy(() => import("./pages/hub/HubAuth"));
const HubDashboard = lazy(() => import("./pages/hub/HubDashboard"));
const HubProprietati = lazy(() => import("./pages/hub/HubProprietati"));
const HubUtilitati = lazy(() => import("./pages/hub/HubUtilitati"));
const HubCredit = lazy(() => import("./pages/hub/HubCredit"));
const HubAsigurari = lazy(() => import("./pages/hub/HubAsigurari"));
const HubMutare = lazy(() => import("./pages/hub/HubMutare"));
const HubSetari = lazy(() => import("./pages/hub/HubSetari"));
import { HubLayout } from "./components/hub/HubLayout";

const queryClient = new QueryClient();

const App = () => {
  useOrderNotifications();
  usePagePreload();

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
        <BrowserRouter>
          <Layout>
            <Suspense fallback={<RouteAwareSkeleton />}>
              <FadeIn>
                <Routes>
                  <Route path="/" element={<Index />} />
                  
                  <Route path="/extras-carte-funciara" element={<OrderExtrasCF />} />
                  <Route path="/confirmare-comanda" element={<OrderConfirmation />} />
                  <Route path="/termeni-si-conditii" element={<TermeniSiConditii />} />
                  <Route path="/politica-confidentialitate" element={<PoliticaConfidentialitate />} />
                  <Route path="/despre-noi" element={<DespreNoi />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/install" element={<Install />} />
                  <Route path="/solutionare-litigii" element={<SolutionareLitigii />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/calificare-credit" element={<CalificareCredit />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/hub/auth" element={<HubAuth />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                {/* Hub Post-Vânzare - separate layout */}
                <Routes>
                  <Route path="/hub" element={<HubLayout />}>
                    <Route index element={<HubDashboard />} />
                    <Route path="proprietati" element={<HubProprietati />} />
                    <Route path="utilitati" element={<HubUtilitati />} />
                    <Route path="credit" element={<HubCredit />} />
                    <Route path="asigurari" element={<HubAsigurari />} />
                    <Route path="mutare" element={<HubMutare />} />
                    <Route path="setari" element={<HubSetari />} />
                  </Route>
                </Routes>
              </FadeIn>
            </Suspense>
          </Layout>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
